import React, { useEffect, useState, useRef } from "react";
import {
  FaGoogle,
  FaSearch,
  FaYoutube,
  FaShoppingCart,
  FaChartLine,
  FaCheckCircle,
  FaArrowRight,
  FaPlay,
  FaMousePointer,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";

/* ── Animated Counter ── */
const Counter = ({ end, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const target = parseInt(end, 10);
    const dur = 1800;
    let startTs = null;
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min((ts - startTs) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(target * ease));
      if (p < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [started, end]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export default function GoogleAds() {


  const services = [
    {
      icon: <FaSearch size={28} />,
      title: "Google Search Ads",
      desc: "Capture high-intent traffic by appearing exactly when customers search for what you offer.",
      gradient: "from-blue-500 to-cyan-400",
      tag: "High Intent",
    },
    {
      icon: <FaYoutube size={28} />,
      title: "YouTube Ads",
      desc: "Build massive brand awareness and engagement with targeted video campaigns.",
      gradient: "from-red-500 to-rose-600",
      tag: "Video Reach",
    },
    {
      icon: <FaShoppingCart size={28} />,
      title: "Google Shopping",
      desc: "Showcase your products directly in search results to drive immediate e-commerce sales.",
      gradient: "from-green-500 to-emerald-400",
      tag: "E-Commerce",
    },
    {
      icon: <FaMapMarkerAlt size={28} />,
      title: "Local Service Ads",
      desc: "Dominate your local market by appearing at the very top of Google for local searches.",
      gradient: "from-yellow-400 to-orange-500",
      tag: "Local Business",
    },
    {
      icon: <FaMousePointer size={28} />,
      title: "Display Network",
      desc: "Retarget past visitors and build brand visibility across millions of websites and apps.",
      gradient: "from-purple-500 to-indigo-500",
      tag: "Retargeting",
    },
    {
      icon: <FaChartLine size={28} />,
      title: "Performance Max",
      desc: "Leverage Google's AI to find converting customers across all Google channels.",
      gradient: "from-blue-600 to-indigo-600",
      tag: "AI Driven",
    },
  ];

  const included = [
    { label: "Deep Keyword Research",     sub: "Finding the exact phrases your buyers are searching for." },
    { label: "Competitor Analysis",       sub: "Studying competitor ads to outbid and outsmart them." },
    { label: "Conversion Tracking Setup", sub: "Flawless tracking via Google Tag Manager and Analytics 4." },
    { label: "Ad Copy & Extensions",      sub: "Writing compelling copy that gets clicks and lowers CPC." },
    { label: "Negative Keywords",         sub: "Filtering out irrelevant traffic to save your budget." },
    { label: "Transparent Reporting",     sub: "Live dashboards so you know exactly what you're paying for." },
  ];

  const process = [
    { num: "01", title: "Discovery",  desc: "We analyze your business, goals, and target audience." },
    { num: "02", title: "Strategy",   desc: "We map out the keywords, bids, and campaign structure." },
    { num: "03", title: "Execution",  desc: "We launch precisely targeted ads with tailored landing pages." },
    { num: "04", title: "Scaling",    desc: "We optimize bids, test ad copy, and scale your winning campaigns." },
  ];

  return (
    <div className="bg-[#030712] min-h-screen text-white pt-24 lg:pt-28 overflow-x-hidden selection:bg-cyan-500 selection:text-white" style={{ fontFamily: 'emoji' }}>


      {/* ── HERO ── */}
      <section className="relative min-h-[90vh] flex items-center border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[140px] animate-orb-drift" />
          <div className="absolute bottom-[-5%] left-[-5%] w-[420px] h-[420px] bg-violet-700/10 rounded-full blur-[120px] animate-orb-drift" style={{ animationDelay: "4s" }} />
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="1" fill="#0EA5E9" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030712]/30 to-[#030712]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-16 lg:py-0">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">

            {/* Left copy */}
            <div className="text-center lg:text-left flex flex-col items-center lg:items-start">
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-black tracking-[0.15em] uppercase mb-8 backdrop-blur-md animate-badge-pop">
                <FaGoogle className="text-sm" />
                Google Partner Agency
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-8">
                <span className="text-white">Dominate</span><br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#38BDF8] to-[#818CF8]">
                  Search Results
                </span><br />
                <span className="text-white">Instantly.</span>
              </h1>

              <h3 className="text-xl sm:text-2xl font-semibold text-cyan-400 mb-4 tracking-wide">
                Capture High-Intent Customers and Maximize Your Sales
              </h3>

              <p className="text-gray-400 text-base md:text-lg leading-relaxed font-light mb-10 max-w-xl">
                We build highly profitable Google Ads campaigns that capture people exactly when they are searching for your products or services.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto relative z-20">
                <Link to="/contact" className="group flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white rounded-2xl font-black tracking-wide hover:scale-105 transition-all duration-300 shadow-[0_0_40px_rgba(14,165,233,0.35)] w-full sm:w-auto">
                  Get My Free Audit
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <a href="https://wa.me/919699938509?text=Hello%20Vishwa%20Digitech!%20I%20want%20to%20talk%20to%20an%20expert%20about%20Google%20Ads." target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold text-gray-300 border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:text-white backdrop-blur-md transition-all duration-300 hover:border-cyan-500/30 w-full sm:w-auto">
                  <FaPlay className="text-cyan-400 text-sm" /> Talk to Ours
                </a>
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mt-12">
                {["Search Network", "Shopping", "YouTube", "Display"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-500 text-xs font-semibold">
                    <FaCheckCircle className="text-cyan-500" />{t}
                  </div>
                ))}
              </div>
            </div>

            {/* Right image */}
            <div className="relative hidden lg:flex items-center justify-center animate-hero-float">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/15 via-red-500/10 to-transparent rounded-[3rem] blur-2xl" />
              <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_30px_100px_rgba(59,130,246,0.15)]">
                <img
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=900&auto=format&fit=crop"
                  alt="Google Ads Analytics Dashboard"
                  className="w-full max-w-[560px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/60 via-transparent to-transparent" />
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-5 -right-5 bg-[#0A0F1F]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-4 shadow-2xl animate-badge-pop">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Conversion Rate</p>
                <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">12.4%</p>
                <p className="text-[10px] text-emerald-400 font-bold mt-1">↑ Top 10% Industry</p>
              </div>
              <div className="absolute -bottom-5 -left-5 bg-[#0A0F1F]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-4 shadow-2xl animate-badge-pop" style={{ animationDelay: "0.2s" }}>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Cost Per Click</p>
                <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">₹12.5</p>
                <p className="text-[10px] text-emerald-400 font-bold mt-1">↓ 40% Optimized</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      

      {/* ── SERVICES ── */}
      <section className="py-24 relative z-10 border-b border-white/5 overflow-hidden">
        <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-blue-600/6 rounded-full blur-[150px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-400 text-xs font-black tracking-widest uppercase mb-6">
              Google Ads Expertise
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white mb-5 leading-[1.1]">
              Capture Demand Across<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-red-400 to-yellow-400">
                Every Google Channel
              </span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto font-light">
              We leverage Google's entire ecosystem to make sure your brand is visible exactly when and where your customers are looking.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-[2rem] border border-white/[0.07] hover:border-white/20 bg-[#0A0F1F]/50 backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_0_50px_rgba(59,130,246,0.1)] p-8"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${svc.gradient} opacity-0 group-hover:opacity-[0.06] transition-opacity duration-700`} />
                <div className="relative z-10">
                  <span className={`inline-block text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded-full bg-gradient-to-r ${svc.gradient} text-white mb-6 shadow-md`}>
                    {svc.tag}
                  </span>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${svc.gradient} p-[1.5px] mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-xl`}>
                    <div className="w-full h-full bg-[#0A0F1F] rounded-2xl flex items-center justify-center text-white">
                      {svc.icon}
                    </div>
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white mb-3 tracking-tight">{svc.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base font-light">{svc.desc}</p>
                  <div className="flex items-center gap-2 mt-6 text-gray-500 group-hover:text-blue-400 text-sm font-bold transition-colors duration-300">
                    Learn more <FaArrowRight className="group-hover:translate-x-1 transition-transform text-xs" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="py-24 relative z-10 border-b border-white/5 bg-white/[0.01]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-400 text-xs font-black tracking-widest uppercase mb-6">
            Methodology
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 tracking-tight">
            How We Drive <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Results</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg font-light mb-16 max-w-xl mx-auto">
            A data-driven approach to ensure every rupee spent brings you a positive return on investment.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {process.map((p, i) => (
              <div key={i} className="relative group bg-[#0A0F1F]/50 rounded-[2rem] border border-white/[0.07] hover:border-blue-500/30 p-8 transition-all duration-300 hover:-translate-y-1 text-left">
                <div className="absolute -inset-[1px] rounded-[2rem] bg-gradient-to-b from-blue-500/0 via-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-[1px]" />
                <div className="relative z-10">
                  <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-blue-500/40 to-cyan-400/20 mb-5 leading-none">{p.num}</p>
                  <h3 className="text-lg font-black text-white mb-2">{p.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ── */}
      <section className="py-24 relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-14 lg:gap-20 items-center">

            <div className="relative w-full lg:w-[48%]">
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-500/12 to-red-500/12 rounded-[3rem] blur-2xl" />
              <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(59,130,246,0.1)]">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&auto=format&fit=crop"
                  alt="Analytics graphs"
                  className="w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/60 via-transparent to-transparent" />
              </div>
            </div>

            <div className="w-full lg:w-[52%]">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-green-500/30 bg-green-500/10 text-green-400 text-xs font-black tracking-widest uppercase mb-6">
                No Hidden Costs
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight leading-[1.1]">
                Everything You Need <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-red-400 to-yellow-400">To Succeed.</span>
              </h2>
              <p className="text-gray-400 mb-8 text-base font-light leading-relaxed">
                We don't just set up ads. We provide a full-service experience that focuses purely on your bottom line.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {included.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 bg-[#0A0F1F]/60 border border-white/[0.06] rounded-2xl px-5 py-4 hover:border-blue-500/30 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0 mt-0.5 shadow-lg">
                      <FaCheckCircle className="text-white text-xs" />
                    </div>
                    <div>
                      <p className="font-black text-white text-sm leading-snug">{item.label}</p>
                      <p className="text-gray-500 text-xs mt-1 leading-relaxed">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

  
    </div>
  );
}